import React, {useEffect, useRef, useState} from 'react';
import {IonContent, IonPage} from '@ionic/react';
import {useHistory} from 'react-router-dom';
import {ApiService} from "../api/ApiService";
import {Trainee} from "../api/model/Trainee";
import LoadDetailHeader, {useTransparentHeaderContrast} from '../components/LoadDetailHeader';
import {ChevronRight, PlusIcon, SeatedIcon} from '../components/icons';
import { useLogPageVisit } from '../utils/logPageVisit';

const EMPTY_TRAINEE: Trainee = {
    currentCourseDay: 0,
    traineeCode: '',
    traineeName: '',
    currentCourseId: 0,
    dateCreated: '',
    dmCode: '',
    passed: 0,
    trainerCode: '',
    trainerName: '',
    released: 0,
    seatedDate: '',
};

const TraineeProfile: React.FC = () => {
    useLogPageVisit('LP Trainee Info', 'User viewed the Trainee Info page');
    const history = useHistory();
    const {onSheet, onIonScroll} = useTransparentHeaderContrast();
    const contentRef = useRef<HTMLIonContentElement>(null);

    const [mounted, setMounted] = useState(false);
    const [viewKey, setViewKey] = useState(0);
    const [noTrainees, setNoTrainees] = useState(false);
    const [view, setView] = useState<'list' | 'detail'>('list');
    const [releasedTrainees, setReleasedTrainees] = useState<Trainee[]>([]);
    const [unreleasedTrainees, setUnreleasedTrainees] = useState<Trainee[]>([]);
    const [isTrainee, setIsTrainee] = useState(false);
    const [trainee, setTrainee] = useState<Trainee>(EMPTY_TRAINEE);

    const username = localStorage.getItem('username')!;

    useEffect(() => {
      const id = requestAnimationFrame(() => {
        setTimeout(() => setMounted(true), 30);
      });
      return () => cancelAnimationFrame(id);
    }, []);

    useEffect(() => {
        setIsTrainee(localStorage.getItem('type') === 'trainee');
        ApiService.getTrainees('traineeProfile', {}).then(it => {
            setNoTrainees(it.length === 0);
            setReleasedTrainees(it.filter(t => t.released !== null));
            setUnreleasedTrainees(it.filter(t => t.released === null));
        });
    }, []);

    function resetViewScroll() {
        contentRef.current?.scrollToTop(0);
    }

    const getTraineeProfile = (traineeCode: string) => {
        ApiService.getTraineeProfile('traineeProfile', traineeCode).then(it => {
            setTrainee(it);
            setViewKey(key => key + 1);
            setView('detail');
            resetViewScroll();
        });
    }

    const goBack = () => {
        if (view === 'detail') {
            setTrainee(EMPTY_TRAINEE);
            setViewKey(key => key + 1);
            setView('list');
            resetViewScroll();
            return;
        }
        if (history.length > 1) history.goBack();
        else history.replace('/Home');
    };

    const releasedDate = (t: Trainee) =>
        t.released ? t.released.toString().replace('T', ' ').slice(0, -14) : 'Not released';

    const listHeroSubtitle = isTrainee
        ? 'View your training progress.'
        : noTrainees
            ? 'No trainees assigned yet.'
            : `${unreleasedTrainees.length} active · ${releasedTrainees.length} released`;

    const headerTitle = view === 'detail' ? (trainee.traineeCode || 'Trainee') : 'Trainee Profile(s)';

    return (
        <IonPage>
            <LoadDetailHeader title={headerTitle} onBack={goBack} onSheet={onSheet} />

            <IonContent
                ref={contentRef}
                fullscreen
                className="design-a-content design-a-content--light"
                scrollEvents={true}
                onIonScroll={onIonScroll}
            >
                <div className={`design-a design-a--detail${mounted ? ' design-a--mounted' : ''}`}>
                    {view === 'detail' ? (
                        <div key={`detail-${viewKey}`}>
                            <div className="home-hero advance-hero">
                                <div className="advance-hero__eyebrow">Trainee profile</div>
                                <div className="advance-hero__title home-display">
                                    {trainee.traineeCode || 'Trainee'}
                                </div>
                                <div className="advance-hero__meta">
                                    {trainee.traineeName || 'Training information'}
                                </div>
                                <div className="home-hero__chips-row" style={{marginTop: 16}}>
                                    <div className="home-hero__chip">
                                        <div className="home-hero__chip-value">{trainee.currentCourseDay}</div>
                                        <div className="home-hero__chip-label">Course Day</div>
                                    </div>
                                    <div className="home-hero__chip">
                                        <div className="home-hero__chip-value">{trainee.passed ? 'Passed' : 'Active'}</div>
                                        <div className="home-hero__chip-label">Status</div>
                                    </div>
                                </div>
                            </div>

                            <div className="home-sheet advance-sheet">
                                <div className="home-card">
                                    <div className="home-section-label" style={{marginTop: 0}}>Trainee</div>
                                    <div className="training-meta-card">
                                        <div className="training-meta-card__row">
                                            <span className="training-meta-card__label">Code</span>
                                            <span className="training-meta-card__value">{trainee.traineeCode || '—'}</span>
                                        </div>
                                        <div className="training-meta-card__divider" />
                                        <div className="training-meta-card__row">
                                            <span className="training-meta-card__label">Name</span>
                                            <span className="training-meta-card__value">{trainee.traineeName || '—'}</span>
                                        </div>
                                        <div className="training-meta-card__divider" />
                                        <div className="training-meta-card__row">
                                            <span className="training-meta-card__label">DM Code</span>
                                            <span className="training-meta-card__value">{trainee.dmCode || '—'}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="home-card">
                                    <div className="home-section-label" style={{marginTop: 0}}>Training</div>
                                    <div className="training-meta-card">
                                        <div className="training-meta-card__row">
                                            <span className="training-meta-card__label">Course Day</span>
                                            <span className="training-meta-card__value">{trainee.currentCourseDay}</span>
                                        </div>
                                        <div className="training-meta-card__divider" />
                                        <div className="training-meta-card__row">
                                            <span className="training-meta-card__label">Passed</span>
                                            <span className="training-meta-card__value">{trainee.passed ? 'Yes' : 'No'}</span>
                                        </div>
                                        <div className="training-meta-card__divider" />
                                        <div className="training-meta-card__row">
                                            <span className="training-meta-card__label">Released</span>
                                            <span className="training-meta-card__value">{releasedDate(trainee)}</span>
                                        </div>
                                    </div>
                                </div>

                                {(trainee.trainerCode || trainee.trainerName) ? (
                                    <div className="home-card">
                                        <div className="home-section-label" style={{marginTop: 0}}>Trainer</div>
                                        <div className="training-meta-card">
                                            <div className="training-meta-card__row">
                                                <span className="training-meta-card__label">Code</span>
                                                <span className="training-meta-card__value">{trainee.trainerCode || '—'}</span>
                                            </div>
                                            <div className="training-meta-card__divider" />
                                            <div className="training-meta-card__row">
                                                <span className="training-meta-card__label">Name</span>
                                                <span className="training-meta-card__value">{trainee.trainerName || '—'}</span>
                                            </div>
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    ) : (
                        <div key={`list-${viewKey}`}>
                            <div className="home-hero advance-hero">
                                <div className="advance-hero__eyebrow">Training</div>
                                <div className="advance-hero__title home-display">Trainee Profiles</div>
                                <div className="advance-hero__meta">{listHeroSubtitle}</div>
                            </div>

                            <div className="home-sheet advance-sheet">
                                {isTrainee ? (
                                    <button type="button" className="home-action" onClick={() => getTraineeProfile(username)}>
                                        <span className="home-action__icon"><SeatedIcon /></span>
                                        <span className="home-action__label">
                                            {username}
                                            <span className="training-trainee-row__meta">
                                                Course Day: {trainee.currentCourseDay}
                                            </span>
                                        </span>
                                        <ChevronRight />
                                    </button>
                                ) : noTrainees ? (
                                    <div className="scoreboard-empty">
                                        <strong>Attention! You Have No Trainees</strong>
                                        <p>Claim a trainee below to begin instruction.</p>
                                        <button
                                            type="button"
                                            className="home-action home-action--tint"
                                            onClick={() => history.push('/newtrainee')}
                                        >
                                            <span className="home-action__icon"><PlusIcon /></span>
                                            <span className="home-action__label">New Trainee</span>
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <button
                                            type="button"
                                            className="home-action home-action--tint"
                                            onClick={() => history.push('/newtrainee')}
                                        >
                                            <span className="home-action__icon"><PlusIcon /></span>
                                            <span className="home-action__label">New Trainee</span>
                                        </button>

                                        <div className="home-section-label">
                                            Unreleased Trainees · {unreleasedTrainees.length}
                                        </div>
                                        <div className="training-trainee-list">
                                            {unreleasedTrainees.map(t => (
                                                <div className="training-trainee-row" key={t.traineeCode}>
                                                    <button type="button" className="home-action" onClick={() => getTraineeProfile(t.traineeCode)}>
                                                        <span className="home-action__icon"><SeatedIcon /></span>
                                                        <span className="home-action__label">
                                                            {t.traineeCode}
                                                            <span className="training-trainee-row__meta">In Training</span>
                                                        </span>
                                                        <ChevronRight />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>

                                        <div className="home-section-label">
                                            Released Trainees · {releasedTrainees.length}
                                        </div>
                                        <div className="training-trainee-list">
                                            {releasedTrainees.map(t => (
                                                <div className="training-trainee-row" key={t.traineeCode}>
                                                    <button type="button" className="home-action" onClick={() => getTraineeProfile(t.traineeCode)}>
                                                        <span className="home-action__icon"><SeatedIcon /></span>
                                                        <span className="home-action__label">
                                                            {t.traineeCode}
                                                            <span className="training-trainee-row__meta">
                                                                Released on: {releasedDate(t)}
                                                            </span>
                                                        </span>
                                                        <ChevronRight />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </IonContent>
        </IonPage>
    );
};

export default TraineeProfile;
